import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faHeart, faCheck } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import {
  getStoredUser,
  getGuestFavorites,
  removeGuestFavorite,
  addProductToCart,
  fetchCartProductIds,
} from '../utils/guestStore';
import { useToast } from './Toast';
import BackButton from './BackButton';
import './FavoritesPage.css';

const FavoritesPage = ({ onBackClick, onCartUpdate }) => {
  const toast = useToast();
  const [favorites, setFavorites] = useState([]);
  const [cartProductIds, setCartProductIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const loadCartIds = useCallback(async () => {
    const ids = await fetchCartProductIds();
    setCartProductIds(ids);
    return ids;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const storedUser = getStoredUser();
      setUser(storedUser);

      try {
        if (storedUser) {
          const [favRes, ids] = await Promise.all([
            fetch(`${API_BASE_URL}?action=favourites&userId=${storedUser.id}`).then((r) => r.json()),
            fetchCartProductIds(),
          ]);
          if (!cancelled) {
            if (favRes.status === 'ok') {
              setFavorites(favRes.favourites || []);
            }
            setCartProductIds(ids);
          }
        } else {
          if (!cancelled) {
            setFavorites(getGuestFavorites());
            setCartProductIds(await fetchCartProductIds());
          }
        }
      } catch (err) {
        console.error('Error loading favorites:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onGuestCartUpdate = async () => {
      await loadCartIds();
      if (!getStoredUser()) {
        setFavorites(getGuestFavorites());
      }
    };
    const onGuestFavoritesUpdate = () => {
      if (!getStoredUser()) {
        setFavorites(getGuestFavorites());
      }
    };
    window.addEventListener('guest-cart-updated', onGuestCartUpdate);
    window.addEventListener('guest-favorites-updated', onGuestFavoritesUpdate);
    return () => {
      window.removeEventListener('guest-cart-updated', onGuestCartUpdate);
      window.removeEventListener('guest-favorites-updated', onGuestFavoritesUpdate);
    };
  }, [loadCartIds]);

  const handleRemoveFavorite = async (productId) => {
    if (!user) {
      setFavorites(removeGuestFavorite(productId));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}?action=remove_favourite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          productId,
        }),
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setFavorites((prev) => prev.filter((fav) => fav.id !== productId));
      }
    } catch (err) {
      console.error('Error removing favorite:', err);
    }
  };

  const handleAddToCart = async (product) => {
    if (cartProductIds.includes(Number(product.id))) {
      return;
    }

    try {
      const result = await addProductToCart(product, 1);
      if (result.ok) {
        toast.success('Added to cart');
        await loadCartIds();
        if (onCartUpdate) onCartUpdate();
      } else {
        toast.error(result.message || 'Failed to add to cart');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error('Error adding to cart');
    }
  };

  const isInCart = (productId) => cartProductIds.includes(Number(productId));

  if (loading) {
    return <div className="favorites-loading">Loading favorites...</div>;
  }

  return (
    <div className="favorites-page">
      <BackButton onClick={onBackClick} />
      <div className="page-title-header">
        <h1>❤️ My Favorites</h1>
      </div>

      {favorites.length === 0 ? (
        <div className="empty-favorites">
          <FontAwesomeIcon icon={faHeart} className="empty-icon" />
          <p>You haven't added any favorites yet.</p>
          <p className="sub-text">Explore products and add them to your favorites!</p>
        </div>
      ) : (
        <div className="favorites-grid">
          {favorites.map((product) => {
            const added = isInCart(product.id);
            return (
              <div key={product.id} className="favorite-card">
                <div className="favorite-image">
                  <img src={product.image_data || product.image} alt={product.name} />
                </div>
                <div className="favorite-info">
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  <div className="favorite-footer">
                    <span className="favorite-price">Rs. {product.price}</span>
                    <div className="favorite-actions">
                      {added ? (
                        <button type="button" className="btn-cart btn-added" disabled>
                          <FontAwesomeIcon icon={faCheck} /> Added to cart
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-cart"
                          onClick={() => handleAddToCart(product)}
                          title="Add to Cart"
                        >
                          <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleRemoveFavorite(product.id)}
                        title="Remove from favourites"
                      >
                        <FontAwesomeIcon icon={faHeart} /> Remove from favourites
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;
